// BL-177 Step 3: hooks for scheduled messages. Reads go straight to the table (row-level security decides who sees what); every change
// announces itself (SCHEDULED_CHANGED_EVENT), so a thread, the Scheduled list and the composer all reload together.
import { useCallback, useEffect, useRef, useState } from 'react';
import { SCHEDULED_CHANGED_EVENT, loadScheduledMessages } from '../../lib/scheduledSendApi';

const REFRESH_MS = 30000;

/** Scheduled messages for one conversation (`sessionId`) or all the person can see (`sessionId` null). */
export function useScheduledMessages({ client, sessionId = null, enabled = true, refreshMs = REFRESH_MS } = {}) {
    const [rows, setRows] = useState([]);
    const [error, setError] = useState('');
    const seq = useRef(0);
    const awaitingRead = useRef(new Map());
    const reload = useCallback(async () => {
        if (!enabled || typeof client?.from !== 'function') return;
        const mine = ++seq.current;
        try {
            const next = await loadScheduledMessages({ client, sessionId });
            if (mine === seq.current) {
                next.forEach(row => awaitingRead.current.delete(row.id));
                setRows([...next, ...awaitingRead.current.values()]); setError('');
            }
        } catch (failure) {
            if (mine === seq.current) setError(failure?.message || 'Scheduled messages could not be loaded.');
        }
    }, [client, sessionId, enabled]);
    useEffect(() => {
        awaitingRead.current.clear();
        setRows([]);
        reload();
        if (typeof window === 'undefined') return undefined;
        window.addEventListener(SCHEDULED_CHANGED_EVENT, reload);
        const timer = refreshMs ? setInterval(reload, refreshMs) : null;
        return () => {
            window.removeEventListener(SCHEDULED_CHANGED_EVENT, reload);
            if (timer) clearInterval(timer);
            seq.current += 1;
        };
    }, [reload, refreshMs]);
    const accept = useCallback((row) => {
        if (!row?.id || (sessionId && row.session_id && row.session_id !== sessionId)) return;
        awaitingRead.current.set(row.id, row);
        setRows(current => [...current.filter(item => item.id !== row.id), row]);
    }, [sessionId]);
    return { rows, error, reload, accept };
}

/** True for a company admin or platform admin (they may cancel anyone's scheduled message in their company). */
export function useIsCompanyAdmin(client) {
    const [isAdmin, setIsAdmin] = useState(false);
    useEffect(() => {
        let active = true;
        if (typeof client?.rpc !== 'function') return undefined;
        Promise.all([client.rpc('is_org_admin'), client.rpc('is_platform_super_admin')])
            .then(([org, platform]) => { if (active) setIsAdmin(org?.data === true || platform?.data === true); })
            .catch(() => {});
        return () => { active = false; };
    }, [client]);
    return isAdmin;
}
